import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { rvApi } from '../api';
import { ROUTES } from '../../../constants/routes';

function RulesViolationReportView() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await rvApi.reportItem(itemId);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePrint = () => {
    window.print();
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
  const fmtDate = (d) => {
    if (!d) return '—';
    const s = String(d).slice(0, 10);
    const [y, m, day] = s.split('-');
    if (y && m && day) return `${m}/${day}/${y}`;
    return d;
  };

  if (loading) {
    return (
      <div className="app-shell-page">
        <div className="app-loading text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !data?.item) {
    return (
      <div className="app-shell-page">
        <div className="alert alert-danger py-2">{error || 'Report not found'}</div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm rv-report-no-print"
          onClick={() => navigate(ROUTES.RULES_VIOLATION_REPORTS)}
        >
          Back to reports
        </button>
      </div>
    );
  }

  const { item, photos, staff } = data;

  return (
    <div className="app-shell-page rv-report-view">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 rv-report-no-print">
        <button
          type="button"
          className="btn btn-sm bg-white text-dark border"
          onClick={() => navigate(ROUTES.RULES_VIOLATION_REPORTS)}
        >
          Back to reports
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={handlePrint}>
          Print
        </button>
      </div>

      <div id="rv-report-print-root" className="card app-card">
        <div className="card-body">
          <h2 className="h5 mb-3">Rules violation report</h2>
          <p className="text-muted small mb-3">
            Violation #{item.violation_number} · Reported {fmt(item.inspected_at)}
          </p>

          <div className="row small mb-4">
            <div className="col-md-6">
              <p className="mb-1"><strong>Community:</strong> {item.property_name}</p>
              <p className="mb-1"><strong>Site:</strong> {item.location_name || '—'}</p>
              <p className="mb-1"><strong>Site address:</strong> {item.location_address || '—'}</p>
              <p className="mb-1"><strong>Resident/guest:</strong> {item.resident_name || '—'}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1"><strong>Group:</strong> {item.group_name}</p>
              <p className="mb-1"><strong>Violation:</strong> {item.violation_type_name}</p>
              <p className="mb-1"><strong>Due date for correction:</strong> {fmtDate(item.due_date_correction)}</p>
              <p className="mb-1"><strong>Community manager:</strong> {staff?.community_manager_name || '—'}</p>
              <p className="mb-1"><strong>Manager email:</strong> {staff?.community_manager_email || '—'}</p>
            </div>
          </div>

          {item.comments && (
            <div className="mb-3">
              <h3 className="h6">Comments</h3>
              <p className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{item.comments}</p>
            </div>
          )}

          {item.witness_information && (
            <div className="mb-3">
              <h3 className="h6">Witness information</h3>
              <p className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{item.witness_information}</p>
            </div>
          )}

          <h3 className="h6 mb-2">Photos</h3>
          {photos?.length ? (
            <div className="row g-3 rv-report-photos">
              {photos.map((ph) => (
                <div key={ph.id} className="col-6 col-md-4">
                  <img
                    src={ph.image_url}
                    alt="Violation"
                    className="img-fluid rounded border w-100"
                    style={{ maxHeight: '280px', objectFit: 'contain' }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted small mb-0">No photos attached to this violation.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RulesViolationReportView;
